import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Minus, Plus, Loader2, Ticket, Shield, AlertCircle, Check, ChevronDown, Download, UserPlus, PartyPopper } from 'lucide-react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from '../../lib/supabase';
import { getStripe } from '../../lib/stripe/client';
import { createTicketPayment, downloadTickets } from '../../lib/stripe/connect';

interface TicketTier {
  name: string;
  price: number;
  quantity: number;
  description?: string;
}

interface TicketPurchaseModalProps {
  event: {
    id: string;
    title: string;
    price: number;
    currency?: string;
    ticket_tiers?: TicketTier[];
    vat_percentage?: number;
    max_participants?: number;
    current_participants?: number;
    sales_start_date?: string;
    sales_end_date?: string;
  };
  onClose: () => void;
  onSuccess: () => void;
}

interface TierSelection {
  [tierName: string]: number;
}

interface AttendeeInfo {
  tierName: string;
  index: number; // which ticket within this tier (0-based)
  name: string;
}

function getCurrencySymbol(currency?: string) {
  switch (currency?.toUpperCase()) {
    case 'GBP': return '£';
    case 'USD': return '$';
    case 'EUR': return '€';
    case 'CHF': return 'CHF ';
    default: return '€';
  }
}

// Payment form component
function CheckoutForm({ onSuccess, onError }: { onSuccess: () => void; onError: (msg: string) => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);

    try {
      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        throw stripeError;
      }

      onSuccess();
    } catch (err: any) {
      onError(err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <button
        type="submit"
        disabled={loading || !stripe}
        className="w-full py-3.5 px-6 bg-accent-text text-white font-semibold text-sm rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-accent-text/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Processing payment...
          </>
        ) : (
          <>
            <Shield className="h-4 w-4" />
            Complete Purchase
          </>
        )}
      </button>
    </form>
  );
}

export function TicketPurchaseModal({ event, onClose, onSuccess }: TicketPurchaseModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top when modal opens
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  const [selections, setSelections] = useState<TierSelection>({});
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'select' | 'auth' | 'details' | 'payment' | 'success'>('select');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [buyerEmail, setBuyerEmail] = useState('');
  const [attendees, setAttendees] = useState<AttendeeInfo[]>([]);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [showFeeBreakdown, setShowFeeBreakdown] = useState(false);
  const [stockInfo, setStockInfo] = useState<Record<string, { total: number; sold: number }>>({});
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const hasTiers = event.ticket_tiers && event.ticket_tiers.length > 0;
  const tiers = hasTiers ? event.ticket_tiers! : [{ name: 'General', price: event.price, quantity: event.max_participants || 100 }];
  const sym = getCurrencySymbol(event.currency);

  // Check for existing pending/processing order from this user for this event
  useEffect(() => {
    const checkExistingOrder = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existingOrder } = await supabase
        .from('ticket_orders')
        .select('id, status, created_at')
        .eq('event_id', event.id)
        .eq('buyer_id', user.id)
        .in('status', ['paid', 'processing'])
        .limit(1)
        .maybeSingle();

      if (existingOrder) {
        setError('You already have tickets for this event. Check your tickets in your profile.');
      }
    };
    checkExistingOrder();
  }, [event.id]);

  // Fetch stock info
  useEffect(() => {
    const fetchStock = async () => {
      const { data } = await supabase
        .from('ticket_tier_stock')
        .select('tier_name, total_quantity, sold_quantity')
        .eq('event_id', event.id);

      if (data) {
        const info: Record<string, { total: number; sold: number }> = {};
        data.forEach(row => {
          info[row.tier_name] = { total: row.total_quantity, sold: row.sold_quantity };
        });
        setStockInfo(info);
      }
    };
    fetchStock();
  }, [event.id]);

  // Get user email, name, and username
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthenticated(!!user);
      if (user?.email) setBuyerEmail(user.email);
      if (user?.id) {
        const { data: profile } = await supabase
          .from('users')
          .select('username')
          .eq('id', user.id)
          .single();
        if (profile?.username) setCurrentUsername(profile.username);
        else setCurrentUsername(user.id);
      }
    };
    getUser();
  }, []);

  // Listen for auth changes (user logs in after being redirected)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {
      if (event === 'SIGNED_IN') {
        const { data: { user } } = await supabase.auth.getUser();
        setIsAuthenticated(!!user);
        if (user?.email) setBuyerEmail(user.email);
        if (user?.id) {
          const { data: profile } = await supabase
            .from('users')
            .select('username')
            .eq('id', user.id)
            .single();
          if (profile?.username) setCurrentUsername(profile.username);
          else setCurrentUsername(user.id);
        }
        // If user was on auth step, go back to select to continue
        if (step === 'auth') {
          setStep('select');
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [step]);

  // Init Stripe
  useEffect(() => {
    setStripePromise(getStripe());
  }, []);

  // Build attendee list whenever selections change
  useEffect(() => {
    setAttendees(prev => {
      const newAttendees: AttendeeInfo[] = [];
      for (const [tierName, qty] of Object.entries(selections)) {
        for (let i = 0; i < qty; i++) {
          // Preserve existing names from previous state (functional update avoids stale closure)
          const existing = prev.find(a => a.tierName === tierName && a.index === i);
          newAttendees.push({
            tierName,
            index: i,
            name: existing?.name || '',
          });
        }
      }
      return newAttendees;
    });
  }, [selections]);

  const updateAttendeeName = (tierName: string, index: number, name: string) => {
    setAttendees(prev => prev.map(a =>
      a.tierName === tierName && a.index === index
        ? { ...a, name }
        : a
    ));
  };

  const updateQuantity = (tierName: string, delta: number) => {
    setSelections(prev => {
      const current = prev[tierName] || 0;
      const newQty = Math.max(0, Math.min(current + delta, 10));
      const next = { ...prev };
      if (newQty === 0) {
        delete next[tierName];
      } else {
        next[tierName] = newQty;
      }
      return next;
    });
  };

  const totalTickets = Object.values(selections).reduce((sum, q) => sum + q, 0);
  const subtotal = tiers.reduce((sum, tier) => {
    const qty = selections[tier.name] || 0;
    return sum + (tier.price * qty);
  }, 0);

  const getAvailable = (tierName: string, tierQuantity: number) => {
    const stock = stockInfo[tierName];
    if (stock) return stock.total - stock.sold;
    return tierQuantity;
  };

  const handleProceedToDetails = () => {
    if (totalTickets === 0) return;
    // Check if user is authenticated, if not show auth step
    if (!isAuthenticated) {
      setStep('auth');
      return;
    }
    setStep('details');
  };

  const allAttendeesNamed = attendees.every(a => a.name.trim().length > 0);

  const handlePurchase = async () => {
    if (!buyerEmail) {
      setError('Please enter your email address');
      return;
    }
    if (!allAttendeesNamed) {
      setError('Please enter the name for every ticket holder');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const ticketSelections = Object.entries(selections)
        .filter(([, qty]) => qty > 0)
        .map(([tierName, quantity]) => ({
          tierName,
          quantity,
          attendeeNames: attendees
            .filter(a => a.tierName === tierName)
            .map(a => a.name.trim()),
        }));

      const result = await createTicketPayment({
        eventId: event.id,
        tickets: ticketSelections,
        buyerName: attendees[0]?.name || '',
        buyerEmail,
      });

      setOrderId(result.orderId);
      setBreakdown(result.breakdown);

      // Free tickets: skip payment step, go straight to success
      if (result.free) {
        setStep('success');
        return;
      }

      setClientSecret(result.clientSecret);
      setStep('payment');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    setStep('success');
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-start justify-center z-[60] sm:p-6 overflow-y-auto">
      <div className="bg-[#FAFCF7] rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] sm:max-h-full overflow-y-auto">

        {/* Header */}
        <div className="relative px-5 pt-5 pb-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-content/40 hover:text-content/70 rounded-full hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-accent-text/10 rounded-xl flex items-center justify-center">
              <Ticket className="h-5 w-5 text-accent-text" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-content">
                {step === 'success' ? 'Purchase Complete!' : 'Get Tickets'}
              </h2>
              <p className="text-xs text-content/50 truncate max-w-[280px]">{event.title}</p>
            </div>
          </div>

          {/* Step indicator */}
          {step !== 'success' && (
            <div className="flex gap-1.5 mt-4">
              {['select', 'auth', 'details', 'payment'].map((s, i) => (
                <div
                  key={s}
                  className={`h-1 flex-1 rounded-full transition-colors ${
                    i <= ['select', 'auth', 'details', 'payment'].indexOf(step)
                      ? 'bg-accent-text'
                      : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="px-5 py-5">

          {/* STEP 1: Tier Selection */}
          {step === 'select' && (
            <div className="space-y-3">
              {tiers.map((tier) => {
                const qty = selections[tier.name] || 0;
                const available = getAvailable(tier.name, tier.quantity);
                const soldOut = available <= 0;

                return (
                  <div
                    key={tier.name}
                    className={`p-4 rounded-xl border transition-all ${
                      qty > 0
                        ? 'border-accent-text/30 bg-accent-text/5'
                        : soldOut
                          ? 'border-gray-100 bg-gray-50 opacity-60'
                          : 'border-gray-100 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-content text-sm">{tier.name}</h3>
                        {tier.description && (
                          <p className="text-xs text-content/50 mt-0.5">{tier.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-base font-bold text-accent-text">
                            {tier.price === 0 ? 'Free' : `${sym}${tier.price.toFixed(2)}`}
                          </span>
                          {!soldOut && (
                            <span className="text-[10px] text-content/35 bg-gray-100 px-1.5 py-0.5 rounded">
                              {available} left
                            </span>
                          )}
                        </div>
                      </div>

                      {soldOut ? (
                        <span className="text-xs font-medium text-red-500 bg-red-50 px-2.5 py-1.5 rounded-lg">
                          Sold out
                        </span>
                      ) : (
                        <div className="flex items-center gap-1.5 bg-gray-100 rounded-lg p-0.5">
                          <button
                            onClick={() => updateQuantity(tier.name, -1)}
                            disabled={qty === 0}
                            className="w-8 h-8 flex items-center justify-center rounded-md text-content/60 hover:bg-white hover:text-content disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <span className="w-6 text-center text-sm font-semibold text-content">{qty}</span>
                          <button
                            onClick={() => updateQuantity(tier.name, 1)}
                            disabled={qty >= Math.min(available, 10)}
                            className="w-8 h-8 flex items-center justify-center rounded-md text-content/60 hover:bg-white hover:text-content disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Summary */}
              {totalTickets > 0 && (
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-content/60">
                      {totalTickets} {totalTickets === 1 ? 'ticket' : 'tickets'}
                    </span>
                    <span className="text-lg font-bold text-content">{sym}{subtotal.toFixed(2)}</span>
                  </div>
                  {subtotal > 0 && (
                    <p className="text-[10px] text-content/35 mt-0.5">+ service fees at checkout</p>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <button
                onClick={handleProceedToDetails}
                disabled={totalTickets === 0}
                className="w-full py-3.5 bg-accent-text text-white font-semibold text-sm rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            </div>
          )}

          {/* STEP: Authentication */}
          {step === 'auth' && (
            <div className="space-y-5 py-2">
              <div className="text-center">
                <div className="w-16 h-16 bg-accent-text/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserPlus className="h-8 w-8 text-accent-text" />
                </div>
                <h3 className="text-xl font-bold text-content mb-2">Create an account</h3>
                <p className="text-sm text-content/60">
                  You'll need an account to get your tickets and manage your bookings.
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
                    onClose();
                    window.dispatchEvent(new CustomEvent('show-auth'));
                  }}
                  className="w-full py-3.5 bg-accent-text text-white font-semibold text-sm rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all"
                >
                  Sign Up
                </button>
                <button
                  onClick={() => {
                    sessionStorage.setItem('post_onboarding_redirect', `${window.location.pathname}${window.location.search}`);
                    onClose();
                    window.dispatchEvent(new CustomEvent('show-auth'));
                  }}
                  className="w-full py-3.5 bg-white border-2 border-accent-text text-accent-text font-semibold text-sm rounded-xl hover:bg-accent-text/5 active:scale-[0.98] transition-all"
                >
                  Log In
                </button>
              </div>

              <button
                onClick={() => { setStep('select'); setError(null); }}
                className="w-full py-2 text-sm text-content/50 hover:text-content/70 transition-colors"
              >
                Back to tickets
              </button>
            </div>
          )}

          {/* STEP 2: Attendee Details */}
          {step === 'details' && (
            <div className="space-y-4">
              {/* Attendee names per ticket */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <UserPlus className="h-4 w-4 text-accent-text" />
                  <h4 className="text-sm font-semibold text-content">Attendee Details</h4>
                </div>
                <p className="text-xs text-content/50 mb-3">
                  Please enter the full name for each ticket holder. Names will appear on the tickets.
                </p>
                <div className="space-y-2.5">
                  {attendees.map((attendee, idx) => {
                    const tierLabel = totalTickets > 1 || Object.keys(selections).length > 1
                      ? `${attendee.tierName} — Ticket ${attendee.index + 1}`
                      : 'Attendee Name';

                    return (
                      <div key={`${attendee.tierName}-${attendee.index}`}>
                        <label className="block text-xs font-medium text-content/70 mb-1">
                          {tierLabel} *
                        </label>
                        <input
                          type="text"
                          value={attendee.name}
                          onChange={(e) => updateAttendeeName(attendee.tierName, attendee.index, e.target.value)}
                          placeholder="Full name"
                          className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 outline-none transition-colors"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-content mb-1.5">Email Address *</label>
                <input
                  type="email"
                  value={buyerEmail}
                  onChange={(e) => setBuyerEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 outline-none transition-colors"
                />
                <p className="text-[10px] text-content/35 mt-1">Tickets will be sent to this email</p>
              </div>

              {/* Order Summary */}
              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <h4 className="text-sm font-semibold text-content mb-3">Order Summary</h4>
                <div className="space-y-2">
                  {tiers.filter(t => (selections[t.name] || 0) > 0).map(tier => (
                    <div key={tier.name} className="flex justify-between text-sm">
                      <span className="text-content/70">
                        {selections[tier.name]}x {tier.name}
                      </span>
                      <span className="font-medium text-content">
                        {sym}{(tier.price * (selections[tier.name] || 0)).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-gray-100 pt-2 flex justify-between">
                    <span className="text-sm font-medium text-content">Subtotal</span>
                    <span className="text-sm font-bold text-content">{sym}{subtotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Terms Agreement */}
              <label className="flex items-start gap-3 p-3.5 bg-gray-50 rounded-xl border border-gray-100 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-accent-text focus:ring-accent-text/20 cursor-pointer flex-shrink-0"
                />
                <span className="text-xs text-content/60 leading-relaxed">
                  I have read and agree to the{' '}
                  <a
                    href="/ticket-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-text underline hover:text-accent-text/80 font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Ticket Purchase Terms & Conditions
                  </a>
                  , including the refund policy, fee structure, and that non-refundable fees apply in the event of a cancellation.
                </span>
              </label>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => { setStep('select'); setError(null); }}
                  className="flex-1 py-3 text-sm font-medium text-content/60 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handlePurchase}
                  disabled={loading || !buyerEmail || !agreedToTerms || !allAttendeesNamed}
                  className="flex-[2] py-3 bg-accent-text text-white font-semibold text-sm rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {subtotal === 0 ? 'Completing registration...' : 'Preparing payment...'}
                    </>
                  ) : (
                    subtotal === 0 ? 'Complete Registration' : 'Proceed to Payment'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Stripe Payment */}
          {step === 'payment' && clientSecret && stripePromise && (
            <div className="space-y-4">
              {/* Fee breakdown */}
              {breakdown && (
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <button
                    onClick={() => setShowFeeBreakdown(!showFeeBreakdown)}
                    className="w-full flex items-center justify-between"
                  >
                    <span className="text-sm font-semibold text-content">Total: {sym}{(breakdown.totalAmount / 100).toFixed(2)}</span>
                    <ChevronDown className={`h-4 w-4 text-content/40 transition-transform ${showFeeBreakdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showFeeBreakdown && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-xs">
                      <div className="flex justify-between text-content/60">
                        <span>Tickets</span>
                        <span>{sym}{(breakdown.subtotal / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-content/60">
                        <span>Service fee</span>
                        <span>{sym}{(breakdown.serviceFee / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-content/60">
                        <span>Commission (2%)</span>
                        <span>{sym}{(breakdown.commissionFee / 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-content/60">
                        <span>Payment processing</span>
                        <span>{sym}{(breakdown.stripeFee / 100).toFixed(2)}</span>
                      </div>
                      {breakdown.vatAmount > 0 && (
                        <div className="flex justify-between text-content/60">
                          <span>VAT (incl.)</span>
                          <span>{sym}{(breakdown.vatAmount / 100).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                <CheckoutForm
                  onSuccess={handlePaymentSuccess}
                  onError={(msg) => setError(msg)}
                />
              </Elements>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex items-center justify-between">
                <button
                  onClick={() => { setStep('details'); setError(null); }}
                  className="text-sm text-content/50 hover:text-content/70 transition-colors"
                >
                  ← Back
                </button>
                <p className="text-[10px] text-content/30">
                  <Shield className="h-3 w-3 inline mr-1" />
                  Secure payment powered by Stripe
                </p>
              </div>
            </div>
          )}

          {/* STEP 4: Success — Thank You */}
          {step === 'success' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-once">
                <PartyPopper className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-content mb-2">Thank You for Your Purchase!</h3>
              <p className="text-sm text-content/60 mb-1">
                Your tickets have been confirmed and are ready.
              </p>
              <p className="text-sm text-content/50 mb-6">
                You can find and download your tickets anytime under{' '}
                <span className="font-semibold text-accent-text">My Tickets</span> on your profile.
              </p>

              <div className="flex flex-col gap-3">
                {orderId && (
                  <button
                    onClick={async () => {
                      try {
                        setDownloadingPdf(true);
                        await downloadTickets(orderId);
                      } catch (err: any) {
                        alert(err.message || 'Failed to download tickets');
                      } finally {
                        setDownloadingPdf(false);
                      }
                    }}
                    disabled={downloadingPdf}
                    className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-accent-text text-white font-semibold text-sm rounded-xl hover:bg-accent-text/90 disabled:opacity-50 transition-colors"
                  >
                    {downloadingPdf ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                    Download Tickets (PDF)
                  </button>
                )}
                <button
                  onClick={() => {
                    onSuccess();
                    if (currentUsername) {
                      navigate(`/profile/${currentUsername}/my-tickets`);
                    }
                  }}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-green-50 text-green-700 font-semibold text-sm rounded-xl hover:bg-green-100 border border-green-200 transition-colors"
                >
                  <Ticket className="h-4 w-4" />
                  Go to My Tickets
                </button>
                <button
                  onClick={() => {
                    onSuccess();
                    onClose();
                  }}
                  className="px-8 py-2.5 text-content/50 font-medium text-xs rounded-xl hover:bg-gray-100 transition-colors"
                >
                  Stay on this page
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
